import type { HttpPort } from "~/lib/http";
import type { StudentCheckin, StudentMonster, StudentToday } from "./studentHome";

export interface StudentMoodDefinition {
  id: string;
  label: string;
  emoji: string;
  type: "feeling" | "trait";
  resonance: string;
}

export interface StudentCheckinSnapshot {
  today: StudentToday;
  moods: StudentMoodDefinition[];
  classGoal: { goal_text: string } | null;
}

export interface MorningCheckinDraft {
  dailyGoal?: string | null;
  dailyTaskId?: string | null;
  taskSkipped?: boolean;
  morningMood?: number | null;
  monsterMoods?: string[] | null;
  monsterMoodCustom?: string | null;
}

export interface MorningCheckinResult {
  checkin: StudentCheckin;
  resonance?: { label: string; emoji: string; message: string } | null;
}

export interface EveningCheckoutDraft {
  weeklyGoalsRating?: number | null;
  mood?: number | null;
  dailyTaskId?: string | null;
  dailyTaskScore?: number | null;
  dailyTaskComment?: string | null;
  classGoalRating?: number | null;
}

export interface EveningCheckoutResult {
  checkin: StudentCheckin & { goal_1_rating?: number | null; class_goal_rating?: number | null; daily_task_score?: number | null };
  xpEarned: number;
  levelUp: boolean;
  monster: StudentMonster | null;
}

const clean = (value?: string | null) => value?.trim() || null;

export function createStudentCheckinModule(http: HttpPort, token: string) {
  return {
    async load(classId: string): Promise<StudentCheckinSnapshot> {
      const [today, moodPayload, classGoal] = await Promise.all([
        http.request<StudentToday>({ path: `/checkins/${classId}/today`, token }),
        http.request<{ moods?: StudentMoodDefinition[] }>({ path: "/checkins/moods", token }).catch(() => ({ moods: [] })),
        http.request<{ goal: { goal_text: string } | null }>({ path: `/class-goals/${classId}/today`, token }).catch(() => ({ goal: null }))
      ]);
      return { today, moods: moodPayload.moods ?? [], classGoal: classGoal.goal };
    },
    morning(classId: string, draft: MorningCheckinDraft): Promise<MorningCheckinResult> {
      return http.request({
        path: `/checkins/${classId}/morning`, method: "POST", token,
        body: {
          dailyGoal: draft.dailyTaskId ? null : clean(draft.dailyGoal),
          dailyTaskId: draft.dailyTaskId || null,
          taskSkipped: draft.taskSkipped === true,
          morningMood: draft.morningMood ?? null,
          monsterMoods: draft.monsterMoods?.slice(0, 3) ?? null,
          monsterMoodCustom: clean(draft.monsterMoodCustom)
        }
      });
    },
    evening(classId: string, draft: EveningCheckoutDraft): Promise<EveningCheckoutResult> {
      return http.request({
        path: `/checkins/${classId}/evening`, method: "POST", token,
        body: {
          weeklyGoalsRating: draft.weeklyGoalsRating ?? null,
          mood: draft.mood ?? null,
          dailyTaskId: draft.dailyTaskId || null,
          dailyTaskScore: draft.dailyTaskScore ?? null,
          dailyTaskComment: clean(draft.dailyTaskComment),
          classGoalRating: draft.classGoalRating ?? null
        }
      });
    },
    chooseTask(classId: string, input: { dailyTaskId?: string | null; dailyGoal?: string | null }): Promise<void> {
      return http.request({ path: `/checkins/${classId}/add-task`, method: "PATCH", token, body: { dailyTaskId: input.dailyTaskId || null, dailyGoal: input.dailyTaskId ? null : clean(input.dailyGoal) } });
    },
    updateMoods(classId: string, input: { monsterMoods?: string[] | null; monsterMoodCustom?: string | null }): Promise<void> {
      return http.request({ path: `/checkins/${classId}/mood-chips`, method: "PATCH", token, body: { monsterMoods: input.monsterMoods?.slice(0, 3) ?? null, monsterMoodCustom: clean(input.monsterMoodCustom) } });
    },
    unskipTask(classId: string): Promise<void> {
      return http.request({ path: `/checkins/${classId}/unskip-task`, method: "PATCH", token, body: {} });
    }
  };
}

export type StudentCheckinModule = ReturnType<typeof createStudentCheckinModule>;
